import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PromisePool } from '@supercharge/promise-pool';
import { Modal } from '@/components/Modal';
import { AddCameraModalContent } from './components/AddCameraModalContent';
import {
    CameraCreationItem,
    CameraCreationStatus,
    CameraConnectionStatus,
} from '@/types/camera';
import { ConnectionStatus } from '@/types/camera';
import { EngineService } from '@/services/engine';
import { useSourceCreationStore } from '@/stores/source-creation.store';
import { toast } from '@/components';
import { DEFAULT_ANALYSIS_SETTINGS } from '@/constants/default-analysis-settings';
import {
    INITIAL_CAMERAS_STATE,
    INITIAL_ACTIVE_CAMERA_ID,
    INITIAL_CAMERA_VALIDATION,
    INITIAL_CONNECTION_STATUS,
} from '@/utils/connect-camera';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useConfiguration } from '@/features/SettingsView/hooks/useConfiguration';

interface CameraSourceModalProps {
    isOpen: boolean;
    targetFolderId?: string;
    onClose: () => void;
}

export function CameraSourceModal({
    isOpen,
    targetFolderId,
    onClose,
}: CameraSourceModalProps) {
    const { t } = useTranslation(I18nNamespace.CAMERAS);
    const [cameras, setCameras] = useState<CameraCreationItem[]>(
        INITIAL_CAMERAS_STATE,
    );
    const [activeCameraId, setActiveCameraId] = useState<string>(
        INITIAL_ACTIVE_CAMERA_ID,
    );
    const [cameraValidation, setCameraValidation] = useState<
        Record<string, boolean>
    >(INITIAL_CAMERA_VALIDATION);
    const [connectionStatus, setConnectionStatus] = useState<
        Record<string, CameraConnectionStatus>
    >(INITIAL_CONNECTION_STATUS);
    const [triggerValidation, setTriggerValidation] = useState(0);
    const nextCameraIdRef = useRef(2);
    const { configuration } = useConfiguration();
    const availableCameraSlots = configuration
        ? Math.max(
              0,
              configuration.maxCameras.value - configuration.currentCameras,
          )
        : Number.POSITIVE_INFINITY;
    const cannotAddAnotherCamera = cameras.length >= availableCameraSlots;
    const exceedsCameraCapacity = cameras.length > availableCameraSlots;

    const handleCameraUpdate = useCallback(
        (cameraId: string, updates: Partial<CameraCreationItem>) => {
            setCameras((prev) =>
                prev.map((camera) =>
                    camera.id === cameraId ? { ...camera, ...updates } : camera,
                ),
            );
        },
        [],
    );

    const handleValidationChange = useCallback(
        (cameraId: string, valid: boolean) => {
            setCameraValidation((prev) => ({ ...prev, [cameraId]: valid }));
        },
        [],
    );

    const handleAddCamera = () => {
        if (cannotAddAnotherCamera) {
            return;
        }
        const newCameraId = String(nextCameraIdRef.current);
        const newCamera: CameraCreationItem = {
            id: newCameraId,
            name: t('defaultName'),
            streamUrl: '',
            username: '',
            password: '',
            ...DEFAULT_ANALYSIS_SETTINGS,
            status: CameraCreationStatus.PENDING,
        };
        setCameras([...cameras, newCamera]);
        setCameraValidation((prev) => ({ ...prev, [newCameraId]: false }));
        setConnectionStatus((prev) => ({
            ...prev,
            [newCameraId]: { status: ConnectionStatus.IDLE },
        }));
        setActiveCameraId(newCameraId);
        nextCameraIdRef.current += 1;
    };

    const handleDeleteCamera = (cameraId: string) => {
        const updatedCameras = cameras.filter((cam) => cam.id !== cameraId);
        setCameras(updatedCameras);

        setCameraValidation((prev) => {
            const { [cameraId]: _, ...rest } = prev;
            return rest;
        });

        setConnectionStatus((prev) => {
            const { [cameraId]: _, ...rest } = prev;
            return rest;
        });

        if (cameraId === activeCameraId) {
            setActiveCameraId(updatedCameras[0].id);
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleRetryConnection = async () => {
        const activeCamera = cameras.find(
            (camera) => camera.id === activeCameraId,
        );
        if (!activeCamera || !activeCamera.streamUrl) {
            return;
        }

        setConnectionStatus((prev) => ({
            ...prev,
            [activeCameraId]: {
                ...prev[activeCameraId],
                status: ConnectionStatus.TESTING,
            },
        }));

        try {
            const fullStreamUrl = `rtsp://${activeCamera.streamUrl}`;
            const result = await EngineService.validateCamera(
                fullStreamUrl,
                activeCamera?.username,
                activeCamera?.password,
            );

            if (result.isValid) {
                setConnectionStatus((prev) => ({
                    ...prev,
                    [activeCameraId]: {
                        status: ConnectionStatus.SUCCESS,
                        lastChecked: new Date(),
                        snapshot: result.snapshotBase64,
                    },
                }));
            } else {
                setConnectionStatus((prev) => ({
                    ...prev,
                    [activeCameraId]: {
                        status: ConnectionStatus.ERROR,
                        message:
                            result.errorMessage || t('errors.failedToConnect'),
                    },
                }));
            }
        } catch (error) {
            console.error('Error testing connection:', error);
            setConnectionStatus((prev) => ({
                ...prev,
                [activeCameraId]: {
                    status: ConnectionStatus.ERROR,
                    message: t('errors.testingConnection'),
                },
            }));
        }
    };

    const handleCheckConnection = async () => {
        const firstInvalidCamera = cameras.find(
            (cam) => !cameraValidation[cam.id],
        );

        if (firstInvalidCamera) {
            setActiveCameraId(firstInvalidCamera.id);
            setTimeout(() => {
                setTriggerValidation((prev) => prev + 1);
            }, 100);
            return;
        }

        setConnectionStatus((prev) =>
            cameras.reduce(
                (acc, cam) => ({
                    ...acc,
                    [cam.id]: {
                        ...prev[cam.id],
                        status: ConnectionStatus.TESTING,
                    },
                }),
                prev,
            ),
        );

        const { results, errors } = await PromisePool.for(cameras)
            .withConcurrency(5)
            .process(async (camera) => {
                const fullStreamUrl = `rtsp://${camera.streamUrl}`;
                const result = await EngineService.validateCamera(
                    fullStreamUrl,
                    camera.username,
                    camera.password,
                );
                return { cameraId: camera.id, result };
            });

        const newConnectionStatus: Record<string, CameraConnectionStatus> = {};

        results.forEach((item) => {
            const { cameraId, result } = item;
            if (result.isValid) {
                newConnectionStatus[cameraId] = {
                    status: ConnectionStatus.SUCCESS,
                    lastChecked: new Date(),
                    snapshot: result.snapshotBase64,
                };
            } else {
                newConnectionStatus[cameraId] = {
                    status: ConnectionStatus.ERROR,
                    message: result.errorMessage || t('errors.failedToConnect'),
                };
            }
        });

        errors.forEach((error) => {
            newConnectionStatus[error.item.id] = {
                status: ConnectionStatus.ERROR,
                message: t('errors.testingConnection'),
            };
        });

        setConnectionStatus((prev) => ({ ...prev, ...newConnectionStatus }));

        if (errors.length > 0) {
            toast.error(t('errors.someFailedToConnect'));
        }
    };

    const allCamerasValid =
        Object.values(cameraValidation).every((valid) => valid) &&
        cameras.length > 0;

    const isTestingConnection = Object.values(connectionStatus).some(
        (status) => status.status === ConnectionStatus.TESTING,
    );

    const checkConnectionButtonText =
        cameras.length === 1
            ? t('modal.checkConnection')
            : t('modal.checkConnections', { count: cameras.length });

    const connectCameraButtonText =
        cameras.length === 1
            ? t('modal.connectCamera')
            : t('modal.connectCameras', { count: cameras.length });

    const handleConnectCamera = async () => {
        if (!allCamerasValid) {
            return;
        }
        if (exceedsCameraCapacity) {
            return;
        }

        useSourceCreationStore.getState().addCameraItems(
            cameras.map((camera) => ({
                name: camera.name,
                streamUrl: `rtsp://${camera.streamUrl}`,
                username: camera.username,
                password: camera.password,
                faceRecognition: camera.faceRecognition,
                licensePlateRecognition: camera.licensePlateRecognition,
                opticalCharacterRecognition: camera.opticalCharacterRecognition,
                analysisFramesPerSecond: camera.analysisFramesPerSecond,
            })),
            targetFolderId,
        );

        setCameras(INITIAL_CAMERAS_STATE);
        setActiveCameraId(INITIAL_ACTIVE_CAMERA_ID);
        setCameraValidation(INITIAL_CAMERA_VALIDATION);
        setConnectionStatus(INITIAL_CONNECTION_STATUS);
        nextCameraIdRef.current = 2;

        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('modal.title')}
            onCancel={handleCancel}
            secondaryButtonText={checkConnectionButtonText}
            onSecondarySubmit={handleCheckConnection}
            secondaryButtonDisabled={isTestingConnection}
            submitText={connectCameraButtonText}
            onSubmit={handleConnectCamera}
            submitDisabled={!allCamerasValid || exceedsCameraCapacity}
            size="large"
            fullHeight
        >
            <AddCameraModalContent
                cameras={cameras}
                activeCameraId={activeCameraId}
                onCameraSelect={setActiveCameraId}
                onAddCamera={handleAddCamera}
                onDeleteCamera={handleDeleteCamera}
                onCameraUpdate={handleCameraUpdate}
                onValidationChange={handleValidationChange}
                connectionStatus={connectionStatus}
                onRetryConnection={handleRetryConnection}
                triggerValidation={triggerValidation}
                addDisabled={cannotAddAnotherCamera}
                atCapHint={cannotAddAnotherCamera ? t('atCapHint') : undefined}
            />
        </Modal>
    );
}
